import { useState, useMemo, useEffect } from "react";
import { ArrowLeft, BarChart3, ChevronDown } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useQuery } from "../../lib/useQuery";
import { supabase } from "../../lib/supabase";
import { calcularEstadoFromLead } from "../../lib/leadEstado";

const MESES = ["ENE", "FEB", "MAR", "ABR", "MAY", "JUN", "JUL", "AGO", "SEPT", "OCT", "NOV", "DIC"];
const ESTADOS_LEAD = ["cliente", "lista_espera", "perdido", "pendiente"];
const ESTADOS_BAJA = ["Procesada", "Pendiente"];

const COLORS = {
  cliente: { bg: "bg-green-500", text: "text-green-600", light: "bg-green-100" },
  lista_espera: { bg: "bg-yellow-400", text: "text-yellow-600", light: "bg-yellow-100" },
  perdido: { bg: "bg-red-500", text: "text-red-600", light: "bg-red-100" },
  pendiente: { bg: "bg-blue-400", text: "text-blue-600", light: "bg-blue-100" },
  Procesada: { bg: "bg-green-500", text: "text-green-600", light: "bg-green-100" },
};

// Usa la fuente única (leadEstado.js) y mapea "convertido" → "cliente"
// para no romper las claves internas de este componente (headers, COLORS).
function calcularEstadoLead(lead) {
  const estado = calcularEstadoFromLead(lead);
  if (estado === "convertido") return "cliente";
  if (estado === "perdido" || estado === "lista_espera") return estado;
  return "pendiente";
}

function calcularEstadoBaja(baja, actionTypes, completions) {
  if (!actionTypes || actionTypes.length === 0) return "Pendiente";
  const bajaCompletions = (completions || []).filter(c => c.baja_id === baja.id && c.completed);
  return bajaCompletions.length >= actionTypes.length ? "Procesada" : "Pendiente";
}

function StatTable({ title, headers, rows, totals, showTasaCierre = true }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
        <h3 className="font-semibold text-gray-800 text-sm">{title}</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-gray-50">
              <th className="px-3 py-2 text-left font-medium text-gray-600"></th>
              {headers.map(h => (
                <th key={h.key} className={`px-3 py-2 text-center font-medium ${h.color || "text-gray-600"}`}>
                  {h.label}
                </th>
              ))}
              <th className="px-3 py-2 text-center font-medium text-gray-600">TOTAL</th>
              {showTasaCierre && <th className="px-3 py-2 text-center font-medium text-gray-600">TASA CIERRE</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i} className="border-t border-gray-100 hover:bg-gray-50">
                <td className="px-3 py-2 font-medium text-gray-700">{row.label}</td>
                {headers.map(h => (
                  <td key={h.key} className="px-3 py-2 text-center text-gray-600">
                    {row.values[h.key] || ""}
                  </td>
                ))}
                <td className="px-3 py-2 text-center font-medium text-gray-700">{row.total}</td>
                {showTasaCierre && (
                  <td className="px-3 py-2 text-center font-medium text-brand">{row.tasaCierre}</td>
                )}
              </tr>
            ))}
          </tbody>
          {totals && (
            <tfoot>
              <tr className="border-t-2 border-gray-200 bg-blue-50 font-semibold">
                <td className="px-3 py-2 text-gray-800">TOTAL</td>
                {headers.map(h => (
                  <td key={h.key} className="px-3 py-2 text-center text-gray-700">{totals.values[h.key] || 0}</td>
                ))}
                <td className="px-3 py-2 text-center text-gray-800">{totals.total}</td>
                {showTasaCierre && <td className="px-3 py-2 text-center text-brand">{totals.tasaCierre}</td>}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

function BarChart({ data, title, colorMap, labelKey = "label", showLegend = true }) {
  const maxTotal = Math.max(...data.map(d => d.total), 1);
  const categories = Object.keys(colorMap);

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <h3 className="font-semibold text-gray-800 text-sm mb-4">{title}</h3>
      {showLegend && (
        <div className="flex flex-wrap gap-3 mb-4 text-xs">
          {categories.map(cat => (
            <div key={cat} className="flex items-center gap-1.5">
              <div className={`w-3 h-3 rounded ${colorMap[cat].bg}`} />
              <span className="text-gray-600 capitalize">{cat.replace("_", " ")}</span>
            </div>
          ))}
        </div>
      )}
      <div className="flex items-end gap-2 h-40">
        {data.map((item, i) => (
          <div key={i} className="flex-1 flex flex-col items-center">
            <div className="w-full flex flex-col-reverse" style={{ height: "120px" }}>
              {categories.map(cat => {
                const value = item.values[cat] || 0;
                const height = maxTotal > 0 ? (value / maxTotal) * 100 : 0;
                return height > 0 ? (
                  <div
                    key={cat}
                    className={`w-full ${colorMap[cat].bg} first:rounded-b last:rounded-t relative group`}
                    style={{ height: `${height}%`, minHeight: value > 0 ? "8px" : 0 }}
                  >
                    {value > 0 && (
                      <span className="absolute inset-0 flex items-center justify-center text-white text-xs font-medium">
                        {value}
                      </span>
                    )}
                  </div>
                ) : null;
              })}
            </div>
            <span className="text-xs text-gray-500 mt-2 font-medium">{item[labelKey]}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SesionPruebaChart({ data }) {
  const categories = ["perdido", "pendiente", "lista_espera", "cliente"];
  const colorMap = COLORS;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <h3 className="font-semibold text-gray-800 text-sm mb-4">Resultados con/sin Sesión Prueba</h3>
      <div className="flex flex-wrap gap-3 mb-4 text-xs">
        {categories.map(cat => (
          <div key={cat} className="flex items-center gap-1.5">
            <div className={`w-3 h-3 rounded ${colorMap[cat].bg}`} />
            <span className="text-gray-600 capitalize">{cat.replace("_", " ")}</span>
          </div>
        ))}
      </div>
      <div className="flex items-end gap-8 justify-center h-40">
        {data.map((item, i) => (
          <div key={i} className="flex flex-col items-center w-24">
            <div className="w-full flex flex-col-reverse h-32 rounded overflow-hidden border border-gray-200">
              {categories.map(cat => {
                const pct = item.percentages[cat] || 0;
                return pct > 0 ? (
                  <div
                    key={cat}
                    className={`w-full ${colorMap[cat].bg} relative`}
                    style={{ height: `${pct}%` }}
                  />
                ) : null;
              })}
            </div>
            <span className="text-xs text-gray-500 mt-2 font-medium text-center">{item.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Estadisticas({ onBack }) {
  const { user } = useAuth();
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);

  const { data: leads, loading: loadingLeads } = useQuery(async () => {
    const { data, error } = await supabase
      .from("leads")
      .select("*")
      .eq("student_id", user.id);
    if (error) throw error;
    return data || [];
  }, [user.id]);

  const { data: bajas, loading: loadingBajas } = useQuery(async () => {
    const { data, error } = await supabase
      .from("bajas")
      .select("*")
      .eq("student_id", user.id);
    if (error) throw error;
    return data || [];
  }, [user.id]);

  const { data: bajaActionTypes, loading: loadingActionTypes } = useQuery(async () => {
    const { data, error } = await supabase
      .from("baja_action_types")
      .select("*")
      .eq("student_id", user.id);
    if (error) throw error;
    return data || [];
  }, [user.id]);

  const { data: bajaCompletions, loading: loadingCompletions } = useQuery(async () => {
    const { data, error } = await supabase
      .from("baja_action_completions")
      .select("*")
      .eq("student_id", user.id);
    if (error) throw error;
    return data || [];
  }, [user.id]);

  const { data: canalOptions } = useQuery(async () => {
    const { data, error } = await supabase
      .from("canal_options")
      .select("*")
      .eq("student_id", user.id)
      .order("sort_order");
    if (error) throw error;
    return data || [];
  }, [user.id]);

  const { data: servicioOptions } = useQuery(async () => {
    const { data, error } = await supabase
      .from("servicio_options")
      .select("*")
      .eq("student_id", user.id)
      .order("sort_order");
    if (error) throw error;
    return data || [];
  }, [user.id]);

  const { data: dicPrevYear } = useQuery(async () => {
    const { data } = await supabase
      .from("monthly_data")
      .select("n_clientes")
      .eq("student_id", user.id)
      .eq("year", year - 1)
      .eq("month", 12)
      .single();
    return data?.n_clientes ?? null;
  }, [user.id, year]);

  const availableYears = useMemo(() => {
    const yearsSet = new Set();
    
    (leads || []).forEach(l => {
      const fecha = l.fecha_contacto || l.created_at;
      if (fecha) yearsSet.add(new Date(fecha).getFullYear());
    });
    
    (bajas || []).forEach(b => {
      if (b.fecha_baja) yearsSet.add(new Date(b.fecha_baja).getFullYear());
    });

    if (yearsSet.size === 0) {
      yearsSet.add(currentYear);
    }

    const minYear = Math.min(...yearsSet);
    yearsSet.add(minYear - 1);

    return Array.from(yearsSet).sort((a, b) => a - b);
  }, [leads, bajas, currentYear]);

  useEffect(() => {
    if (availableYears.length > 0 && !availableYears.includes(year)) {
      setYear(availableYears[availableYears.length - 1]);
    }
  }, [availableYears, year]);

  const clientesCierreAnterior = dicPrevYear;

  const filteredLeads = useMemo(() => {
    if (!leads) return [];
    return leads.filter(l => {
      const fecha = l.fecha_contacto || l.created_at;
      if (!fecha) return false;
      return new Date(fecha).getFullYear() === year;
    });
  }, [leads, year]);

  const filteredBajas = useMemo(() => {
    if (!bajas) return [];
    return bajas.filter(b => {
      if (!b.fecha_baja) return false;
      return new Date(b.fecha_baja).getFullYear() === year;
    });
  }, [bajas, year]);

  const leadsByMonth = useMemo(() => {
    const byMonth = MESES.map((mes, idx) => {
      const monthLeads = filteredLeads.filter(l => {
        const fecha = l.fecha_contacto || l.created_at;
        return new Date(fecha).getMonth() === idx;
      });
      const values = {};
      ESTADOS_LEAD.forEach(e => {
        values[e] = monthLeads.filter(l => calcularEstadoLead(l) === e).length;
      });
      const total = monthLeads.length;
      const convertidos = values.cliente || 0;
      const tasaCierre = total > 0 ? `${((convertidos / total) * 100).toFixed(1)}%` : "0,0%";
      return { label: mes, values, total, tasaCierre };
    });

    const totals = { values: {}, total: 0 };
    ESTADOS_LEAD.forEach(e => {
      totals.values[e] = byMonth.reduce((sum, m) => sum + (m.values[e] || 0), 0);
    });
    totals.total = byMonth.reduce((sum, m) => sum + m.total, 0);
    const totalConvertidos = totals.values.cliente || 0;
    totals.tasaCierre = totals.total > 0 ? `${((totalConvertidos / totals.total) * 100).toFixed(1)}%` : "0,0%";

    return { rows: byMonth, totals };
  }, [filteredLeads]);

  const leadsByCanal = useMemo(() => {
    const canales = canalOptions?.map(c => c.name) || [];
    const rows = canales.map(canal => {
      const canalLeads = filteredLeads.filter(l => l.canal === canal);
      const values = {};
      ESTADOS_LEAD.forEach(e => {
        values[e] = canalLeads.filter(l => calcularEstadoLead(l) === e).length;
      });
      const total = canalLeads.length;
      const convertidos = values.cliente || 0;
      const tasaCierre = total > 0 ? `${((convertidos / total) * 100).toFixed(1)}%` : "0,0%";
      return { label: canal, values, total, tasaCierre };
    });

    const totals = { values: {}, total: 0 };
    ESTADOS_LEAD.forEach(e => {
      totals.values[e] = rows.reduce((sum, r) => sum + (r.values[e] || 0), 0);
    });
    totals.total = rows.reduce((sum, r) => sum + r.total, 0);
    const totalConvertidos = totals.values.cliente || 0;
    totals.tasaCierre = totals.total > 0 ? `${((totalConvertidos / totals.total) * 100).toFixed(1)}%` : "0,0%";

    return { rows, totals };
  }, [filteredLeads, canalOptions]);

  const leadsByServicio = useMemo(() => {
    const servicios = servicioOptions?.map(s => s.name) || [];
    const rows = servicios.map(servicio => {
      const servicioLeads = filteredLeads.filter(l => l.servicio === servicio);
      const values = {};
      ESTADOS_LEAD.forEach(e => {
        values[e] = servicioLeads.filter(l => calcularEstadoLead(l) === e).length;
      });
      const total = servicioLeads.length;
      const convertidos = values.cliente || 0;
      const tasaCierre = total > 0 ? `${((convertidos / total) * 100).toFixed(1)}%` : "0,0%";
      return { label: servicio, values, total, tasaCierre };
    });

    const totals = { values: {}, total: 0 };
    ESTADOS_LEAD.forEach(e => {
      totals.values[e] = rows.reduce((sum, r) => sum + (r.values[e] || 0), 0);
    });
    totals.total = rows.reduce((sum, r) => sum + r.total, 0);
    const totalConvertidos = totals.values.cliente || 0;
    totals.tasaCierre = totals.total > 0 ? `${((totalConvertidos / totals.total) * 100).toFixed(1)}%` : "0,0%";

    return { rows, totals };
  }, [filteredLeads, servicioOptions]);

  const bajasByMonth = useMemo(() => {
    const byMonth = MESES.map((mes, idx) => {
      const monthBajas = filteredBajas.filter(b => new Date(b.fecha_baja).getMonth() === idx);
      const values = {};
      ESTADOS_BAJA.forEach(e => {
        values[e] = monthBajas.filter(b => calcularEstadoBaja(b, bajaActionTypes, bajaCompletions) === e).length;
      });
      const total = monthBajas.length;
      return { label: mes, values, total };
    });

    const totals = { values: {}, total: 0 };
    ESTADOS_BAJA.forEach(e => {
      totals.values[e] = byMonth.reduce((sum, m) => sum + (m.values[e] || 0), 0);
    });
    totals.total = byMonth.reduce((sum, m) => sum + m.total, 0);

    return { rows: byMonth, totals };
  }, [filteredBajas, bajaActionTypes, bajaCompletions]);

  const sesionPruebaStats = useMemo(() => {
    const conSesion = filteredLeads.filter(l => l.sesion_prueba === true);
    const sinSesion = filteredLeads.filter(l => l.sesion_prueba !== true);

    const calcStats = (leadsArr) => {
      const values = {};
      const percentages = {};
      ESTADOS_LEAD.forEach(e => {
        values[e] = leadsArr.filter(l => calcularEstadoLead(l) === e).length;
      });
      const total = leadsArr.length;
      ESTADOS_LEAD.forEach(e => {
        percentages[e] = total > 0 ? (values[e] / total) * 100 : 0;
      });
      const convertidos = values.cliente || 0;
      const tasaCierre = total > 0 ? `${((convertidos / total) * 100).toFixed(1)}%` : "0,0%";
      return { values, percentages, total, tasaCierre };
    };

    const conStats = calcStats(conSesion);
    const sinStats = calcStats(sinSesion);

    return {
      conSesion: { ...conStats, label: "% CON SESIÓN" },
      sinSesion: { ...sinStats, label: "% SIN SESIÓN" },
      rows: [
        {
          label: "% CON SESIÓN",
          values: Object.fromEntries(ESTADOS_LEAD.map(e => [e, conStats.total > 0 ? `${((conStats.values[e] / conStats.total) * 100).toFixed(1)}%` : "0,0%"])),
          total: conStats.total,
          tasaCierre: conStats.tasaCierre,
        },
        {
          label: "% SIN SESIÓN",
          values: Object.fromEntries(ESTADOS_LEAD.map(e => [e, sinStats.total > 0 ? `${((sinStats.values[e] / sinStats.total) * 100).toFixed(1)}%` : "0,0%"])),
          total: sinStats.total,
          tasaCierre: sinStats.tasaCierre,
        },
        {
          label: "CON SESIÓN",
          values: conStats.values,
          total: conStats.total,
          tasaCierre: conStats.tasaCierre,
        },
        {
          label: "SIN SESIÓN",
          values: sinStats.values,
          total: sinStats.total,
          tasaCierre: sinStats.tasaCierre,
        },
      ],
      chartData: [
        { label: "% CON SESIÓN", percentages: conStats.percentages },
        { label: "% SIN SESIÓN", percentages: sinStats.percentages },
      ],
    };
  }, [filteredLeads]);

  const loading = loadingLeads || loadingBajas || loadingActionTypes || loadingCompletions;

  const leadHeaders = [
    { key: "cliente", label: "Convertido", color: "text-green-600" },
    { key: "lista_espera", label: "Lista Espera", color: "text-yellow-600" },
    { key: "perdido", label: "Perdido", color: "text-red-600" },
    { key: "pendiente", label: "Pendiente", color: "text-blue-600" },
  ];

  const bajaHeaders = [
    { key: "Procesada", label: "Procesada", color: "text-green-600" },
    { key: "Pendiente", label: "Pendiente", color: "text-blue-600" },
  ];

  return (
    <div className="animate-fade-in">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={onBack} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
          <ArrowLeft size={20} className="text-gray-600" />
        </button>
        <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
          <BarChart3 size={20} className="text-indigo-600" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-gray-900">Estadísticas</h2>
          <p className="text-sm text-gray-500">Análisis de leads, clientes y bajas</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <div className="relative">
            <select
              value={year}
              onChange={e => setYear(Number(e.target.value))}
              className="appearance-none px-4 py-2 pr-8 text-sm font-medium bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand/20 focus:border-brand cursor-pointer"
            >
              {availableYears.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <ChevronDown size={16} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-2 border-brand border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="space-y-8">
          <section>
            <h3 className="text-base font-semibold text-gray-800 mb-4">Leads por Meses</h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <StatTable
                title="Desglose mensual"
                headers={leadHeaders}
                rows={leadsByMonth.rows}
                totals={leadsByMonth.totals}
              />
              <BarChart
                data={leadsByMonth.rows.filter(r => r.total > 0)}
                title="LEADS POR MESES"
                colorMap={COLORS}
              />
            </div>
          </section>

          <section>
            <h3 className="text-base font-semibold text-gray-800 mb-4">Leads por Tipo de Contacto</h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <StatTable
                title="Por canal"
                headers={leadHeaders}
                rows={leadsByCanal.rows}
                totals={leadsByCanal.totals}
              />
              <BarChart
                data={leadsByCanal.rows.filter(r => r.total > 0)}
                title="LEADS POR TIPO DE CONTACTO"
                colorMap={COLORS}
              />
            </div>
          </section>

          <section>
            <h3 className="text-base font-semibold text-gray-800 mb-4">Leads por Tipo de Servicio</h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <StatTable
                title="Por servicio"
                headers={leadHeaders}
                rows={leadsByServicio.rows}
                totals={leadsByServicio.totals}
              />
              <BarChart
                data={leadsByServicio.rows.filter(r => r.total > 0)}
                title="LEADS POR TIPO DE SERVICIO"
                colorMap={COLORS}
              />
            </div>
          </section>

          <section>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-gray-800">Bajas por Meses</h3>
              <div className="text-xs text-gray-500 bg-gray-100 px-3 py-1.5 rounded-lg">
                Clientes dic. {year - 1}: <span className="font-semibold text-gray-700">{clientesCierreAnterior != null ? clientesCierreAnterior : "—"}</span>
                {clientesCierreAnterior == null && (
                  <span className="ml-2 text-amber-600">Introduce el dato en Clientes Mensuales</span>
                )}
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <StatTable
                title="Desglose mensual"
                headers={bajaHeaders}
                rows={bajasByMonth.rows}
                totals={bajasByMonth.totals}
                showTasaCierre={false}
              />
              <BarChart
                data={bajasByMonth.rows.filter(r => r.total > 0)}
                title="BAJAS POR MESES"
                colorMap={{
                  Procesada: COLORS.Procesada,
                  Pendiente: COLORS.pendiente,
                }}
              />
            </div>
          </section>

          <section>
            <h3 className="text-base font-semibold text-gray-800 mb-4">Sesión Prueba</h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <StatTable
                title="Resultados con/sin sesión prueba"
                headers={leadHeaders}
                rows={sesionPruebaStats.rows}
              />
              <SesionPruebaChart data={sesionPruebaStats.chartData} />
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
